import debounce from 'lodash/debounce'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import I18n from '../../../resources/i18n/I18n'

import { calculateTotalAddresses, extractAgencyIds, getExpandedItemIds, getFilteredAddresses } from '../../../utils/agencyUtils'
import { CONSTANTS } from '../../../utils/constants'
import './AddressTreeView.scss'
import PatrimonyEditList from './PatrimonyEditList'

const EstateTreeView = ({
  addresses,
  searchTerm,
  selectedItems = [],
  onSelectionChange,
  onSearchChange,
  user,
  type,
  title,
  associatedAgencies = [],
  expandedItems,
  setExpandedItems,
}) => {
  const [previousExpandedItems, setPreviousExpandedItems] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || '')

  const userAgencies = user && user.agencies ? user.agencies : []

  const filteredAddresses = useMemo(
    () => getFilteredAddresses(addresses, searchTerm, type, associatedAgencies, selectedItems),
    [addresses, searchTerm, type, associatedAgencies, selectedItems],
  )

  useEffect(() => {
    if (searchTerm) {
      setIsSearching(true)
      if (!previousExpandedItems.length) {
        setPreviousExpandedItems(expandedItems)
      }
      const itemsToExpand = getExpandedItemIds(filteredAddresses)
      setExpandedItems([...new Set(itemsToExpand)])
    } else if (isSearching) {
      // When search ends, collapse all
      setExpandedItems([])
      setPreviousExpandedItems([])
      setIsSearching(false)
    }
  }, [searchTerm, filteredAddresses])

  const isAgencyDisabled = useCallback(
    (agency) => {
      return type === CONSTANTS.AGENCY_TYPES.AVAILABLE && associatedAgencies.includes(agency.id)
    },
    [type, associatedAgencies],
  )

  const totalAddresses = useMemo(
    () => calculateTotalAddresses(filteredAddresses, type, associatedAgencies, selectedItems),
    [filteredAddresses, type, associatedAgencies, selectedItems],
  )

  const getAllAgencyIds = useCallback(
    () => extractAgencyIds(filteredAddresses, type, associatedAgencies, selectedItems),
    [filteredAddresses, type, associatedAgencies, selectedItems],
  )

  const onToggleExpand = useCallback((itemId) => {
    setExpandedItems((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]))
  }, [])

  // ✅ Debounced search handler
  const debouncedSearch = useMemo(() => debounce(onSearchChange, 500), [onSearchChange])

  // ✅ Cancel debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  useEffect(() => {
    if (localSearchTerm !== searchTerm) {
      debouncedSearch(localSearchTerm)
    }
  }, [localSearchTerm, debouncedSearch])

  return (
    <div className="tree-container">
      <h3 className="mb-3">{title}</h3>

      <div className="tree-content">
        <div className="mb-3 p-3 bg-white rounded shadow-sm">
          <Form.Check
            type="checkbox"
            id="select-all"
            checked={selectedItems.length > 0 && selectedItems.length === getAllAgencyIds().length}
            onChange={() => {
              onSelectionChange(selectedItems.length === getAllAgencyIds().length ? [] : getAllAgencyIds())
            }}
            label={`${I18n.t('AllAddresses')} (${totalAddresses})`}
          />
        </div>

        <div className="mb-3">
          <Form.Control
            type="text"
            placeholder={I18n.t('Search')}
            value={localSearchTerm}
            onChange={({ target }) => setLocalSearchTerm(target.value)}
            className="rounded shadow-sm"
          />
        </div>

        <PatrimonyEditList
          key={`${type}-${associatedAgencies.length}`}
          patrimonies={filteredAddresses}
          searchTerm={searchTerm}
          selectedItems={selectedItems}
          expandedItems={expandedItems}
          onSelectionChange={onSelectionChange}
          onToggleExpand={onToggleExpand}
          type={type}
          userAgencies={userAgencies}
          associatedAgencies={associatedAgencies}
          isAgencyDisabled={isAgencyDisabled}
        />
      </div>
    </div>
  )
}

export default EstateTreeView
